import React from "react";
import { Link } from "react-router-dom";
import { SITE } from "../../config/site";

import mapDiscovery from "../../assets/landing/map-discovery.png";
import mapCategories from "../../assets/landing/map-categories.png";
import mapFiltered from "../../assets/landing/map-filtered.png";
import eventSearch from "../../assets/landing/event-search.png";
import createPlan from "../../assets/landing/create-plan.png";
import hostNow from "../../assets/landing/host-now.png";
import drift from "../../assets/landing/drift.png";
import friendActivities from "../../assets/landing/friend-activities.png";

/* -------------------------------------------------------------------------- */
/* Shared                                                                     */
/* -------------------------------------------------------------------------- */

function AppStoreButton({
  className = "",
  children = "Download on the App Store",
}) {
  return (
    <a
      href={SITE.appStoreUrl}
      target="_blank"
      rel="noreferrer"
      className={`
        inline-flex min-h-12 items-center justify-center
        rounded-2xl bg-[rgb(var(--brand))]
        px-6 py-3
        text-sm font-black text-white
        shadow-[var(--shadow-soft)]
        transition
        hover:-translate-y-0.5 hover:opacity-90
        ${className}
      `}
    >
      {children}
    </a>
  );
}

function Eyebrow({ children, tone = "brand" }) {
  const tones = {
    brand: "text-[rgb(var(--brand))]",
    event: "text-[rgb(var(--event))]",
    plan: "text-[rgb(var(--plan))]",
    intent: "text-[rgb(var(--intent))]",
    cool: "text-[rgb(var(--cool))]",
  };

  return (
    <div
      className={`
        text-xs font-black uppercase tracking-[0.2em]
        ${tones[tone] || tones.brand}
      `}
    >
      {children}
    </div>
  );
}

function SectionHeading({
  eyebrow,
  tone,
  title,
  children,
  center = false,
}) {
  return (
    <div
      className={`
        max-w-2xl
        ${center ? "mx-auto text-center" : ""}
      `}
    >
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>

      <h2
        className="
          mt-3
          text-3xl font-black
          leading-[1.03] tracking-[-0.045em]
          sm:text-4xl
          lg:text-[2.9rem]
        "
      >
        {title}
      </h2>

      {children ? (
        <p
          className="
            mt-5
            text-base leading-7
            text-white/52
            sm:text-lg
          "
        >
          {children}
        </p>
      ) : null}
    </div>
  );
}

function ScreenshotCrop({
  src,
  alt,
  className = "",
  imageClassName = "",
}) {
  return (
    <div
      className={`
        overflow-hidden
        rounded-[1.75rem]
        border border-white/[0.08]
        bg-[#111915]
        shadow-[var(--shadow-soft)]
        ${className}
      `}
    >
      <img
        src={src}
        alt={alt}
        className={`
          block h-full w-full object-cover
          ${imageClassName}
        `}
      />
    </div>
  );
}

function Header() {
  return (
    <header
      className="
        relative z-50
        mx-auto flex w-full max-w-7xl
        items-center justify-between
        px-5 py-5
        sm:px-7
        lg:px-10
      "
    >
      <Link
        to="/"
        aria-label="Veacon home"
        className="flex items-center gap-3"
      >
        <div
          className="
            flex h-11 w-11 items-center justify-center
            overflow-hidden rounded-2xl
            border border-white/10
            bg-white/[0.04]
          "
        >
          <img src="/logo.svg" alt="" className="h-7 w-7" />
        </div>

        <div>
          <div className="text-base font-black tracking-tight">
            Veacon
          </div>

          <div className="hidden text-xs font-bold text-white/45 sm:block">
            Find things to do.
          </div>
        </div>
      </Link>

      <div className="flex items-center gap-2 sm:gap-3">
        <a
          href="#discover"
          className="
            hidden px-3 py-2
            text-sm font-bold text-white/50
            transition hover:text-white
            md:inline-flex
          "
        >
          Explore
        </a>

        <Link
          to="/contact"
          className="
            hidden px-3 py-2
            text-sm font-bold text-white/50
            transition hover:text-white
            sm:inline-flex
          "
        >
          Contact
        </Link>

        <AppStoreButton className="min-h-10 px-4 py-2 text-xs sm:text-sm">
          Get Veacon
        </AppStoreButton>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Hero                                                                       */
/* -------------------------------------------------------------------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div
        className="
          pointer-events-none absolute
          left-1/2 top-[-360px]
          h-[760px] w-[760px]
          -translate-x-1/2
          rounded-full blur-3xl
        "
        style={{
          background:
            "radial-gradient(circle, rgb(var(--brand) / 0.14), transparent 68%)",
        }}
      />

      <Header />

      <div
        className="
          relative mx-auto w-full max-w-5xl
          px-5 pb-24 pt-16
          text-center
          sm:px-7 sm:pb-28 sm:pt-20
          lg:px-10 lg:pb-32 lg:pt-24
        "
      >
        <div
          className="
            mx-auto inline-flex items-center gap-2
            rounded-full
            border border-white/10
            bg-white/[0.035]
            px-3 py-2
            text-xs font-black text-white/60
          "
        >
          <span className="h-2 w-2 rounded-full bg-[rgb(var(--brand))]" />
          Available now for iPhone
        </div>

        <h1
          className="
            mx-auto mt-7 max-w-4xl
            text-[3.4rem] font-black
            leading-[0.93] tracking-[-0.065em]
            sm:text-6xl
            lg:text-[5rem]
          "
        >
          Find things to do.
          <br />
          <span className="text-white/42">
            Make plans. Meet there.
          </span>
        </h1>

        <p
          className="
            mx-auto mt-7 max-w-2xl
            text-base leading-7
            text-white/55
            sm:text-lg sm:leading-8
          "
        >
          Discover what&apos;s happening around you,
          narrow it down, involve friends, and turn an
          idea into something you actually do.
        </p>

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <AppStoreButton />

          <a
            href="#discover"
            className="
              inline-flex min-h-12 items-center justify-center
              rounded-2xl
              border border-white/10
              bg-white/[0.035]
              px-6 py-3
              text-sm font-black
              transition hover:bg-white/[0.07]
            "
          >
            Explore Veacon ↓
          </a>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Map Discovery                                                              */
/* -------------------------------------------------------------------------- */

function DiscoverSection() {
  return (
    <section
      id="discover"
      className="
        border-t border-white/[0.06]
        px-5 py-24
        sm:px-7 sm:py-28
        lg:px-10
      "
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Explore nearby"
          tone="cool"
          title="See what's happening around you."
          center
        >
          Events, plans, and people hosting all live on
          the same map.
        </SectionHeading>

        {/*
          Main map appears once.
          The image is shifted upward inside the crop so
          the phone status bar sits outside the visible area.
        */}
        <div className="mx-auto mt-12 w-full max-w-[360px] sm:max-w-[420px]">
  <div
    className="
      overflow-hidden
      rounded-[2rem]
      border border-white/[0.08]
      bg-[#111915]
      shadow-[var(--shadow-product)]
    "
  >
    <img
      src={mapDiscovery}
      alt="Veacon map showing events, plans, and hosting nearby"
      className="block h-auto w-full"
    />
  </div>
</div>

        <div
          className="
            mt-28 grid
            items-center gap-12
            lg:grid-cols-[0.8fr_1.2fr]
            lg:gap-20
          "
        >
          <div>
            <SectionHeading
              eyebrow="Refine"
              tone="event"
              title="Narrow it down."
            >
              Choose the kinds of events you want to see
              and let the map focus around them.
            </SectionHeading>
          </div>

          <div
            className="
              relative mx-auto
              h-[410px] w-full max-w-[590px]
              sm:h-[470px]
            "
          >
            <ScreenshotCrop
              src={mapFiltered}
              alt="Veacon map after filtering event categories"
              className="
                absolute right-0 top-0
                h-[360px] w-[61%]
                sm:h-[420px]
              "
              imageClassName="object-cover object-[center_25%]"
            />

            <ScreenshotCrop
              src={mapCategories}
              alt="Choosing Gaming and Music event categories"
              className="
                absolute bottom-0 left-0 z-10
                h-[300px] w-[57%]
                sm:h-[350px]
              "
              imageClassName="object-cover object-[center_31%]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Event Search                                                               */
/* -------------------------------------------------------------------------- */

function EventsSection() {
  return (
    <section
      className="
        border-t border-white/[0.06]
        bg-white/[0.012]
        px-5 py-24
        sm:px-7 sm:py-28
        lg:px-10
      "
    >
      <div
        className="
          mx-auto grid max-w-5xl
          items-center gap-14
          lg:grid-cols-[0.8fr_1.2fr]
          lg:gap-20
        "
      >
        <ScreenshotCrop
          src={eventSearch}
          alt="Searching for Boston events in Veacon"
          className="
            mx-auto
            h-[430px] w-full max-w-[300px]
            sm:h-[470px]
          "
          imageClassName="object-cover object-[center_15%]"
        />

        <SectionHeading
          eyebrow="Events"
          tone="event"
          title="Or search it up."
        >
          Search by place or what you&apos;re interested
          in and see what&apos;s coming up.
        </SectionHeading>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Plan / Host                                                                */
/* -------------------------------------------------------------------------- */

function PlanHostSection() {
  return (
    <section
      className="
        border-t border-white/[0.06]
        px-5 py-24
        sm:px-7 sm:py-28
        lg:px-10
      "
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Make it happen"
          tone="plan"
          title="Plan or host."
          center
        >
          Make something for later, or start something now.
        </SectionHeading>

        <div
          className="
            mx-auto mt-14 grid max-w-4xl
            gap-6
            md:grid-cols-2
          "
        >
          <article
            className="
              rounded-[2rem]
              border border-[rgb(var(--plan)/0.18)]
              bg-[rgb(var(--plan)/0.045)]
              p-5
              sm:p-6
            "
          >
            <Eyebrow tone="plan">
              Plan
            </Eyebrow>

            <h3 className="mt-2 text-2xl font-black tracking-tight">
              Put it on the calendar.
            </h3>

            <p className="mt-2 text-sm leading-6 text-white/45">
              Choose where, who can join, and when.
            </p>

            {/*
              Plan and Host intentionally show their complete images.
              These are constrained by width rather than cropped.
            */}
            <div
              className="
                mx-auto mt-6
                w-full max-w-[300px]
                overflow-hidden
                rounded-[1.65rem]
                border border-white/[0.07]
                bg-[#111915]
                shadow-[var(--shadow-soft)]
              "
            >
              <img
                src={createPlan}
                alt="Creating a plan in Veacon"
                className="block h-auto w-full"
              />
            </div>
          </article>

          <article
            className="
              rounded-[2rem]
              border border-[rgb(var(--brand)/0.18)]
              bg-[rgb(var(--brand)/0.045)]
              p-5
              sm:p-6
            "
          >
            <Eyebrow tone="brand">
              Host
            </Eyebrow>

            <h3 className="mt-2 text-2xl font-black tracking-tight">
              Start something now.
            </h3>

            <p className="mt-2 text-sm leading-6 text-white/45">
              Pick the place, set the details, and go.
            </p>

            <div
              className="
                mx-auto mt-6
                w-full max-w-[300px]
                overflow-hidden
                rounded-[1.65rem]
                border border-white/[0.07]
                bg-[#111915]
                shadow-[var(--shadow-soft)]
              "
            >
              <img
                src={hostNow}
                alt="Hosting something now in Veacon"
                className="block h-auto w-full"
              />
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Drift + Friends                                                            */
/* -------------------------------------------------------------------------- */

function DriftFriendsSection() {
  return (
    <section
      className="
        border-t border-white/[0.06]
        bg-white/[0.012]
        px-5 py-24
        sm:px-7 sm:py-28
        lg:px-10
      "
    >
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-2">
          <article
            className="
              overflow-hidden
              rounded-[2.25rem]
              border border-[rgb(var(--intent)/0.18)]
              bg-[rgb(var(--intent)/0.04)]
              p-6
              sm:p-8
            "
          >
            <Eyebrow tone="intent">
              Drift
            </Eyebrow>

            <h2
              className="
                mt-3
                text-3xl font-black
                leading-[1.04] tracking-[-0.04em]
              "
            >
              Not sure what to do?
            </h2>

            <p
              className="
                mt-4 max-w-md
                text-sm leading-6
                text-white/48
                sm:text-base
              "
            >
              Prompt some quesitons to make some ideas
            </p>

            {/*
              Crop away the iOS status bar and bottom navigation.
              The recommendation content is the product story.
            */}
            <ScreenshotCrop
              src={drift}
              alt="Veacon Drift recommendations"
              className="mt-7 h-[390px]"
              imageClassName="object-cover object-[center_18%]"
            />
          </article>

          <article
            className="
              overflow-hidden
              rounded-[2.25rem]
              border border-[rgb(var(--brand)/0.18)]
              bg-[rgb(var(--brand)/0.04)]
              p-6
              sm:p-8
            "
          >
            <Eyebrow tone="brand">
              Friends
            </Eyebrow>

            <h2
              className="
                mt-3
                text-3xl font-black
                leading-[1.04] tracking-[-0.04em]
              "
            >
              See what friends are into.
            </h2>

            <p
              className="
                mt-4 max-w-md
                text-sm leading-6
                text-white/48
                sm:text-base
              "
            >
              See friend ideas
            </p>

            <ScreenshotCrop
              src={friendActivities}
              alt="Activities shared between friends in Veacon"
              className="mt-7 h-[390px]"
              imageClassName="object-cover object-[center_18%]"
            />
          </article>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Home Map                                                                   */
/* -------------------------------------------------------------------------- */


/* -------------------------------------------------------------------------- */
/* CTA                                                                        */
/* -------------------------------------------------------------------------- */

function DownloadCTA() {
  return (
    <section
      className="
        border-t border-white/[0.06]
        px-5 py-24
        sm:px-7 sm:py-28
        lg:px-10
      "
    >
      <div
        className="
          relative mx-auto max-w-5xl
          overflow-hidden
          rounded-[2.5rem]
          border border-white/[0.08]
          bg-[#111915]
          px-6 py-16
          text-center
          shadow-[var(--shadow-product)]
          sm:px-10 sm:py-20
        "
      >
        <div
          className="
            pointer-events-none absolute
            left-1/2 top-[-260px]
            h-[540px] w-[540px]
            -translate-x-1/2
            rounded-full blur-3xl
          "
          style={{
            background:
              "radial-gradient(circle, rgb(var(--brand) / 0.16), transparent 68%)",
          }}
        />

        <div className="relative">
          <Eyebrow tone="brand">
            Veacon for iPhone
          </Eyebrow>

          <h2
            className="
              mx-auto mt-4 max-w-3xl
              text-4xl font-black
              leading-[0.98] tracking-[-0.05em]
              sm:text-5xl
              lg:text-6xl
            "
          >
            Find something.
            <br />
            Make it happen.
          </h2>

          <p
            className="
              mx-auto mt-6 max-w-xl
              text-base leading-7
              text-white/50
              sm:text-lg
            "
          >
            Discover whats nearby, involve friends, and do it!
          </p>

          <div className="mt-8">
            <AppStoreButton />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer                                                                     */
/* -------------------------------------------------------------------------- */

function Footer() {
  return (
    <footer
      className="
        border-t border-white/[0.06]
        px-5 py-10
        sm:px-7
        lg:px-10
      "
    >
      <div
        className="
          mx-auto flex max-w-7xl
          flex-col gap-7
          sm:flex-row sm:items-center sm:justify-between
        "
      >
        <Link
          to="/"
          className="flex items-center gap-3"
        >
          <div
            className="
              flex h-10 w-10 items-center justify-center
              rounded-2xl
              border border-white/10
              bg-white/[0.04]
            "
          >
            <img
              src="/logo.svg"
              alt=""
              className="h-6 w-6"
            />
          </div>

          <div>
            <div className="text-sm font-black">
              Veacon
            </div>

            <div className="text-xs text-white/38">
              Find things to do
            </div>
          </div>
        </Link>

        <div
          className="
            flex flex-wrap
            gap-x-5 gap-y-3
            text-sm font-bold
            text-white/42
          "
        >
          <Link
            to="/contact"
            className="transition hover:text-white"
          >
            Contact
          </Link>

          <Link
            to="/privacy"
            className="transition hover:text-white"
          >
            Privacy
          </Link>

          <Link
            to="/terms"
            className="transition hover:text-white"
          >
            Terms
          </Link>

          <a
            href={SITE.social.instagram}
            target="_blank"
            rel="noreferrer"
            className="transition hover:text-white"
          >
            Instagram
          </a>

          <a
            href={SITE.social.tiktok}
            target="_blank"
            rel="noreferrer"
            className="transition hover:text-white"
          >
            TikTok
          </a>

          <a
            href={SITE.social.discord}
            target="_blank"
            rel="noreferrer"
            className="transition hover:text-white"
          >
            Discord
          </a>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/* Home                                                                       */
/* -------------------------------------------------------------------------- */

export default function Home() {
  return (
    <div
      className="
        dark min-h-screen overflow-x-hidden
        bg-[#0b0f0d]
        text-white
        selection:bg-[rgb(var(--brand)/0.3)]
        selection:text-white
      "
    >
      <Hero />

      <main>
        <DiscoverSection />
        <EventsSection />
        <PlanHostSection />
        <DriftFriendsSection />
        <DownloadCTA />
      </main>

      <Footer />
    </div>
  );
}